/** Brand colors. UI chrome from the Taodyne logo; buttons/primary and `highlights` from the XL mascot. */
export const colors = {
  primary: "#023CFB",
  primaryDark: "#0226C9",
  accent: "#24A095",
  background: "#FAF9F7",
  backgroundBottom: "#E6E6E4",
  backgroundFoot: "#A6A6A4",
  surface: "#FFFFFF",
  text: "#1A1A1A",
  textMuted: "#797979",
  success: "#5B7A0E",
  warning: "#E25229",
  error: "#B9052D",
  highlights: {
    red: "#FF3B30",
    orange: "#EE5120",
    yellow: "#F9E748",
    green: "#36F119",
    greenDark: "#2DB816",
    cyan: "#5BF7FB",
    blue: "#023CFB",
    magenta: "#E36BF5",
  },
} as const;

/** OCR region highlights on document previews (web + mobile). */
export const ocrOverlayColors = {
  regionBorder: "rgba(165, 214, 167, 0.95)",
  regionFill: "rgba(65, 114, 67, 0.92)",
  regionAccent: "#417243",
  toggle: "#A5D6A7",
  displayText: "#E8F5E9",
  fallbackScrim: "rgba(0, 0, 0, 0.88)",
} as const;

export const spacing = {
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
} as const;

export const typography = {
  fontFamily: "Inter, system-ui, sans-serif",
  tagline: "Just dwim it.",
} as const;

/** Semantic colors for document processing status badges (web + mobile). */
export const documentStatusColors = {
  pending: colors.textMuted,
  processing: colors.warning,
  ready: colors.success,
  failed: colors.error,
} as const;

export type DocumentBadgeSurface = "light" | "dark";

type DocumentStatusKey = keyof typeof documentStatusColors;

const STATUS_TINT_ALPHA = 0x22 / 0xff;

/** Append an alpha channel to a `#RRGGBB` hex color (e.g. `22` ≈ 13% opacity). */
export function hexWithAlpha(hex: string, alpha: string): string {
  if (/^#[0-9A-Fa-f]{6}$/.test(hex)) {
    return `${hex}${alpha}`;
  }
  return hex;
}

function blendHex(background: string, foreground: string, opacity: number): string {
  const parse = (hex: string): [number, number, number] => {
    const normalized = hex.replace("#", "");
    return [
      Number.parseInt(normalized.slice(0, 2), 16),
      Number.parseInt(normalized.slice(2, 4), 16),
      Number.parseInt(normalized.slice(4, 6), 16),
    ];
  };
  const [br, bg, bb] = parse(background);
  const [fr, fg, fb] = parse(foreground);
  const channel = (base: number, tint: number) =>
    Math.round(base * (1 - opacity) + tint * opacity);
  const toHex = (value: number) => value.toString(16).padStart(2, "0");
  return `#${toHex(channel(br, fr))}${toHex(channel(bg, fg))}${toHex(channel(bb, fb))}`;
}

/** Tinted pill colors for a status badge on light UI or dark overlays (viewer). */
export function documentStatusBadgeAppearance(
  status: DocumentStatusKey,
  surface: DocumentBadgeSurface = "light",
): { textColor: string; backgroundColor: string } {
  const textColor = documentStatusColors[status];
  const backgroundColor =
    surface === "light"
      ? hexWithAlpha(textColor, "22")
      : blendHex(colors.background, textColor, STATUS_TINT_ALPHA);
  return { textColor, backgroundColor };
}
